import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { MobileModal } from 'components/UI/Modal/MobileModal'
import { NoteManager } from 'components/Notes/NoteManager'
import SearchPage from 'pages/app/mobile/search'
import apiService from 'services/api'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import { FbLink } from 'components/FbUI/FbLink'
import { MobileColors } from 'styles/mobileTokens'

type FormValues = {
  note: string
  attachments: File[]
}

const HeaderSection = styled.div`
  padding: 8px;
`

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #191d23;
`

const CompanyLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`

// Editor drawer styles (inline version of MobileNoteEditorDrawer)
const DrawerContainer = styled.div`
  background: white;
  display: flex;
  flex-direction: column;
  flex: 1;
`

const EditorContainer = styled.div`
  flex: 1;
  padding: 0 16px;
  overflow-y: auto;

  /* Override editor styles for mobile */
  .mobile-note-editor {
    min-height: 200px;
    height: auto;
    padding: 12px;
  }

  /* Move toolbar to bottom and style it */
  .ql-toolbar {
    position: sticky;
    bottom: 0;
    background: white;
    border: none;
    border-top: 1px solid ${MobileColors.borderDefault};
    padding: 8px 0;
  }

  .ql-container {
    border: none;
    font-size: 16px;
  }

  .ql-editor {
    padding: 0;
    min-height: 150px;
  }

  .ql-editor.ql-blank::before {
    color: ${MobileColors.textSecondary};
    font-style: normal;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px;
`

const CancelButton = styled.button`
  flex: 1;
  padding: 14px 24px;
  border: 1px solid ${MobileColors.borderDefault};
  border-radius: 8px;
  background: white;
  font-size: 16px;
  font-weight: 500;
  color: ${MobileColors.textPrimary};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const SaveButton = styled.button`
  flex: 1.5;
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  background: #2c6b4e;
  font-size: 16px;
  font-weight: 600;
  color: white;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #245a42;
  }

  &:disabled {
    background: #2c6b4e;
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export function MobileCreateNotePage() {
  const navigate = useNavigate()
  const api = apiService()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()

  // Check for pre-selected chain from query param
  const preselectedChainId = searchParams.get('chain_id')
    ? Number(searchParams.get('chain_id'))
    : undefined

  const [selectedSearchResult, setSelectedSearchResult] = useState<
    ContactCompanySearchResult | DoorSearchResult | undefined
  >()

  // Determine if the selected result is a door or company
  const isDoorSelected =
    selectedSearchResult && 'chain_proxy_id' in selectedSearchResult

  // Get chain data for company selection
  const chainId = !isDoorSelected
    ? Number(selectedSearchResult?.item_id) || preselectedChainId
    : undefined
  const { data: chainData } = api.useGetChain(chainId)

  // Get door data for door selection
  const restaurantId = isDoorSelected
    ? String((selectedSearchResult as DoorSearchResult).id)
    : undefined
  const { data: doorProxy } = api.useGetPlaceV2(restaurantId)

  // Get the contact_company_id and door_proxy_id
  const contactCompanyId = isDoorSelected
    ? doorProxy?.chain_proxy?.id
    : chainData?.contact_company_id

  const doorProxyId = isDoorSelected ? doorProxy?.id : undefined

  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: {
      note: '',
      attachments: [],
    },
  })

  const noteContent = watch('note')
  const hasContent = noteContent && noteContent.trim().length > 0

  // Mutation for creating note
  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (!contactCompanyId) {
        throw new Error('No company selected')
      }

      return await api.createNote(
        chainId,
        contactCompanyId,
        values.note,
        values.attachments,
        doorProxyId
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notes-infinite'] })
      toast.success('Note created successfully')
      navigate(-1)
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create note')
    },
  })

  const isSubmitting = mutation.isPending

  const onSubmit = handleSubmit(async (values) => {
    await mutation.mutateAsync(values)
  })

  const handleClick = (
    result: ContactCompanySearchResult | DoorSearchResult
  ) => {
    setSelectedSearchResult(result)
  }

  // Handle preselected chain
  useEffect(() => {
    if (preselectedChainId && chainData) {
      setSelectedSearchResult({
        item_id: preselectedChainId,
        name: chainData.chain || '',
        domain: chainData.domain || '',
        contact_company_id: chainData.contact_company_id,
      } as ContactCompanySearchResult)
    }
  }, [preselectedChainId, chainData])

  const companyDisplay = isDoorSelected
    ? doorProxy?.chain_proxy?.name || ''
    : selectedSearchResult?.name || ''

  const doorDisplay = isDoorSelected
    ? (selectedSearchResult as DoorSearchResult)?.name ||
      doorProxy?.restaurant?.restaurant_name ||
      ''
    : ''

  return (
    <MobileModal
      handleBack={() => {
        if (selectedSearchResult && !preselectedChainId) {
          setSelectedSearchResult(undefined)
          reset({ note: '', attachments: [] })
        } else {
          navigate(-1)
        }
      }}
      header={
        <HeaderSection>
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Create a note
          </div>
          {!selectedSearchResult ? (
            <PageTitle>Choose a company</PageTitle>
          ) : (
            <>
              <PageTitle>Add a new note</PageTitle>
              <CompanyLinks>
                {contactCompanyId && (
                  <FbLink to={`/chain/${chainId || contactCompanyId}`}>
                    {companyDisplay}
                  </FbLink>
                )}
                {isDoorSelected && doorProxyId && (
                  <>
                    <span className="text-gray-400">/</span>
                    <FbLink to={`/restaurant/${doorProxyId}`}>
                      {doorDisplay}
                    </FbLink>
                  </>
                )}
              </CompanyLinks>
            </>
          )}
        </HeaderSection>
      }
      footer={null}
    >
      {!selectedSearchResult && !preselectedChainId ? (
        <div className="w-full">
          <SearchPage hideBack onClick={handleClick} />
        </div>
      ) : (
        <DrawerContainer>
          <EditorContainer>
            <NoteManager<FormValues>
              control={control}
              noteName="note"
              attachmentsName="attachments"
              label=""
              placeholder="Start writing note here"
              viewMode={false}
              editorClassName="mobile-note-editor"
              enableMentions
              disabled={isSubmitting}
            />
          </EditorContainer>
          <ButtonContainer>
            <CancelButton
              onClick={() => {
                if (!preselectedChainId) {
                  setSelectedSearchResult(undefined)
                  reset({ note: '', attachments: [] })
                } else {
                  navigate(-1)
                }
              }}
              disabled={isSubmitting}
            >
              Cancel
            </CancelButton>
            <SaveButton
              onClick={onSubmit}
              disabled={isSubmitting || !hasContent}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </SaveButton>
          </ButtonContainer>
        </DrawerContainer>
      )}
    </MobileModal>
  )
}
